import React from 'react';
import { motion } from 'framer-motion';

const ParentShoppingGuiltLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Parent Shopping Guilt: Finding Balance Between Self-Care and Family Needs
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Track Your Parent Guilt Score</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Understand when spending on yourself is self-care, not selfishness. Get personalized insights for healthy financial boundaries.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Purchase
            </motion.button>
          </div>
          <div className="text-8xl">👨‍👩‍👧‍👦</div>
        </div>
      </div>
      
      {/* Why parents feel shopping guilt */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Why Parents Feel Guilty About Personal Spending</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💰</span>
              <div>
                <h3 className="font-bold dark:text-white">Limited Family Budget</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Every dollar spent on yourself feels like it's taken from your children's needs or future.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🧸</span>
              <div>
                <h3 className="font-bold dark:text-white">Children's Endless Wants</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Kids constantly need or want something, making personal purchases feel frivolous.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏰</span>
              <div>
                <h3 className="font-bold dark:text-white">Time Scarcity</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Spending time shopping for yourself adds to the guilt when family time is already limited.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🏆</span>
              <div>
                <h3 className="font-bold dark:text-white">Parental Martyrdom</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  The expectation that "good parents" always put their children's needs ahead of their own.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* How Our Tool Helps */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">How Our Parent Guilt Calculator Helps</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Spending Emotions
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Identify when guilt, rather than financial reality, is driving your spending decisions.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Calculate Your Guilt-to-Benefit Ratio
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Analyze whether purchases genuinely improve your well-being and capacity to parent effectively.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Get Balanced Spending Advice
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Receive personalized tips for creating healthy financial boundaries between family needs and personal care.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Model Healthy Financial Behavior
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Learn how balanced self-spending teaches children important lessons about self-worth and financial health.
            </p>
          </div>
        </div>
      </section>
      
      {/* Parent Shopper Types */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Discover Your Parent Shopper Type</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🎁</span>
              <h3 className="font-bold mt-2 dark:text-white">The Child-Centric Spender</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You freely spend on your children but agonize over the smallest personal purchase, even necessities.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">🙈</span>
              <h3 className="font-bold mt-2 dark:text-white">The Secret Splurger</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You hide personal purchases or downplay their cost due to guilt, creating financial secrecy.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⏱️</span>
              <h3 className="font-bold mt-2 dark:text-white">The "One Day" Postponer</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You constantly defer personal needs and wants, promising "one day" when the kids are older or finances improve.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="text-center mb-3">
              <span className="text-3xl">⚖️</span>
              <h3 className="font-bold mt-2 dark:text-white">The Balanced Budgeter</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              You've found a healthy equilibrium, allocating resources thoughtfully between family needs and personal care.
            </p>
          </div>
        </div>
      </section>
      
      {/* Expert Perspective Section */}
      <section className="mb-8 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-3 dark:text-white">The Expert Perspective</h2>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
          <p className="text-gray-700 dark:text-gray-300 italic mb-3">
            "Parents who practice healthy self-care, including reasonable spending on themselves, are modeling essential life skills for their children. The guilt many parents feel about personal purchases is often disproportionate to the actual financial impact."
          </p>
          <div className="flex items-center">
            <div className="text-2xl mr-3">👩‍⚕️</div>
            <div>
              <p className="font-semibold dark:text-white">Dr. Sarah Chen</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Family Psychologist</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">From Guilt to Balance: Parent Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I was spending thousands on my kids but felt guilty buying a $30 shirt for myself. Tracking my purchases helped me see this irrational guilt pattern and create healthier boundaries."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Michael T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Father of three</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I realized I was teaching my daughter that moms don't deserve nice things. Now I budget for both our needs, and I'm modeling better self-worth while still being financially responsible."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jessica M.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Single mom</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Parent Shopping Guilt Facts */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Parent Spending By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">78%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of parents feel guilty about personal purchases</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$1,732</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual spending parents defer for themselves</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">43%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of parents hide personal purchases from family</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">86%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report improved well-being with balanced spending</p>
          </div>
        </div>
      </section>
      
      {/* FAQ Section - important for SEO */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Parent Shopping Guilt</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is it wrong to spend money on myself when my kids need things?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Meeting your basic needs and reasonable wants isn't selfish—it's necessary self-care. Our calculator helps differentiate between essential self-care and excessive spending, finding balance that serves both you and your family.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I stop feeling guilty about buying things for myself?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Track your spending patterns with our parent guilt calculator to identify when guilt (rather than financial reality) is driving decisions. Create a specific "parent self-care" budget category, and remember that your wellbeing directly impacts your parenting capacity.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">What percentage of the family budget should go to parents' personal needs?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              While there's no universal rule, financial experts suggest each parent should have 5-10% of disposable income for personal discretionary spending. Our calculator helps you find the right balance based on your specific family situation and values.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How do I teach my kids about balanced spending?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Children learn financial habits by watching you. When they see you thoughtfully allocate resources for everyone's needs—including your own—they learn healthy financial boundaries. Our tool helps you model balanced spending decisions that benefit the whole family.
            </p>
          </div>
        </div>
      </section>
      
      {/* CTA section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Balance Family & Self-Care?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your purchases and transform your relationship with parent shopping guilt.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your Parent Guilt Score
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Parent Shopping Guilt</h2>
          <p className="mb-3">
            Parent shopping guilt is the emotional discomfort experienced when purchasing things for yourself while raising children. This feeling often stems from societal expectations, financial concerns, and the pressure to always prioritize children's needs over your own.
          </p>
          <p className="mb-3">
            Our Parent Guilt Calculator helps you understand and address these feelings by tracking your emotional state during purchases, analyzing your spending patterns, and providing personalized strategies to create healthier boundaries between family needs and self-care.
          </p>
          <p>
            Whether you're struggling with excessive self-sacrifice, financial anxiety, or finding a balanced approach to family spending, our free tool helps you develop healthier spending habits that benefit both you and your children.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ParentShoppingGuiltLanding;
